import OrderSellerRemarksFlag from "@/enum/OrderSellerRemarksFlag";
import mainServerClient from "@/services/http/mainServerClient";
import { UndeliverableArea } from "../../setting/unDeliverableArea";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import { WrapperList } from "@/utils/utilityTypes";
import { ProductSkuUpdatedInfoType } from "../pickList";
import { QueryOrderListParams } from "../../order";
import TaskStatusEnum from "@/enum/TaskStatus";

export enum PurchasingLabelStatisticsExcludeType {
    EXCLUDE_ON_HOLD_PRINTED = "EXCLUDE_ON_HOLD_PRINTED",
    EXCLUDE_PACK_LIST_PRINTED = "EXCLUDE_PACK_LIST_PRINTED",
    EXCLUDE_REFUNDING_PRINTED = "EXCLUDE_REFUNDING_PRINTED",
    EXCLUDE_WAYBILL_NO_PRINTED = "EXCLUDE_WAYBILL_NO_PRINTED",
    EXCLUDE_UNDELIVERABLE_AREA = "EXCLUDE_UNDELIVERABLE_AREA",
    EXCLUDE_PRE_SHIPPING = "EXCLUDE_PRE_SHIPPING",
    EXCLUDE_PURCHASING_LABEL_PRINTED = "EXCLUDE_PURCHASING_LABEL_PRINTED",
}

export enum PurchasingLabelStatisticsExportItem {
    ITEM_IMAGE = "ITEM_IMAGE",
    ITEM_TITLE = "ITEM_TITLE",
    QUANTITY = "QUANTITY",
    SELLER_CODE = "SELLER_CODE",
    SIMPLE_IMAGE = "SIMPLE_IMAGE",
    SIMPLE_PRICE = "SIMPLE_PRICE",
    SIMPLE_PROPERTY = "SIMPLE_PROPERTY",
    SIMPLE_SELLER_CODE = "SIMPLE_SELLER_CODE",
    SELLER_REMARK = "SELLER_REMARK",
    BUYER_REMARK = "BUYER_REMARK",
    TOTAL = "TOTAL",
}

export interface PurchasingLabelModel {
    /** 商品唯一码 */
    id: string;
    /** 店铺ID */
    shopId: number;
    /** 订单ID */
    orderId: number;
    /** 订单商品项ID */
    orderItemId: number;
    /** 订单序号 */
    orderSeq: string;
    /** 订单商品项序号 */
    orderItemSeq: string;
    /** 商品标题 */
    title: string;
    /** 商品编码 */
    productExternalId: string;
    /** SKU编码 */
    skuExternalId: string;
    /** SKU属性 */
    skuProperties: string;
    /** 数量 */
    quantity: number;
    /** 市场 */
    market: string;
    /** 档口 */
    wholesaler: string;
    /** 平台订单编号 */
    orderOriginalId: string;
    /** 付款时间 */
    paymentTime: string;
    /** 卖家备注 */
    sellerRemarks: string;
    /** 买家留言 */
    buyerRemarks: string;
    /** 分拣码 */
    sortationCode: string;
    /** 合并状态 */
    mergeSplitStatus: OrderMergeSplitStatus;
    /** 单件/多件 */
    multipleItemFlag: "SINGLE" | "MULTIPLE";
    /** 是否完成扫描 */
    readyStatus: boolean;
    /** 订单商品总数 */
    totalQuantity: number;
    dateCreated: string;

    /** 前端计算的值 */
    _labelIndex?: number;
    _totalLabelCount?: number;
}

export interface PurchasingLabelStatisticsSkuModel {
    market: string;
    wholesaler: string;
    externalId: string;
    name: string;
    originalId?: string;
    picture: string;
    price: number;
    quantity: number;
    stockNo?: string;
}

export interface MoreFeatureExportPurchasingLabelParams {
    shopIds?: number[];
    paidStart?: string;
    paidEnd?: string;
    market?: string;
    wholesaler?: string;
    excludeTypes?: PurchasingLabelStatisticsExcludeType[];
    sellerFlags?: OrderSellerRemarksFlag[];
    undeliverableArea: UndeliverableArea;
    exportItems: PurchasingLabelStatisticsExportItem[];
}

export interface PurchasingOrderSellerRemarkModel {
    orderOriginalId: string;
    flag: OrderSellerRemarksFlag;
    content: string;
}

export interface PurchasingOrderBuyerRemarkModel {
    orderOriginalId: string;
    content: string;
}

export interface PurchasingLabelSkuModel {
    picture: string;
    name: string;
    originalId: string;
    externalId: string;
    quantity: number;
    price: number;
    orderSellerRemarks: PurchasingOrderSellerRemarkModel[];
    orderBuyerRemarks: PurchasingOrderBuyerRemarkModel[];
}

export interface PurchasingLabelProductModel {
    picture: string;
    title: string;
    originalId: string;
    externalId: string;
    total: number;
    skus: PurchasingLabelSkuModel[];
}

export interface StatsModel {
    market: string;
    wholesaler: string;
    marketWholesaler: string;
    total: number;
    productModels: PurchasingLabelProductModel[];
}

interface StatsTaskCacheResultStatsModel {
    distributedId: number;
    status: TaskStatusEnum;
    data: StatsModel[];
}

export interface SelectedOrderTablePurchasingLabelExportParams {
    exportItems: PurchasingLabelStatisticsExportItem[];
    sellerFlags: OrderSellerRemarksFlag[];

    orderIdsMap?: {
        [shopId: number]: number[];
    };
    excludedOrderItemIdsMap?: {
        [shop: number]: number[];
    };
}

export interface SearchAllOrderTablePurchasingLabelExportParams {
    exportItems: PurchasingLabelStatisticsExportItem[];
    listParamsMap?: {
        [shop: number]: QueryOrderListParams;
    };
}

export type OrderTablePurchasingLabelParams =
    | SelectedOrderTablePurchasingLabelExportParams
    | SearchAllOrderTablePurchasingLabelExportParams;

type ExportPackListParamsType = MoreFeatureExportPurchasingLabelParams | OrderTablePurchasingLabelParams;

/** 提交统计发货标签任务 */
export function generatePurchasingLabelExportTask(
    params: ExportPackListParamsType,
): Promise<StatsTaskCacheResultStatsModel> {
    return mainServerClient.post("/purchasing_label/submit_stats_task", params);
}

export function getPurchasingLabelExportTaskProcess(distributedId: number): Promise<StatsTaskCacheResultStatsModel> {
    return mainServerClient.get(`/purchasing_label/get_stats_task_result/${distributedId}`);
}

export enum PurchasingLabelServerType {
    TMALL_CLOUD = "TMALL_CLOUD",
    PDD_CLOUD = "PDD_CLOUD",
    JD_CLOUD = "JD_CLOUD",
    OTHER = "OTHER",
    DOUDIAN_CLOUD = "DOUDIAN_CLOUD",
}

/** 获取拿货标签打印数据 */
export function getPurchasingLabelPrintData(
    distributedId: number,
    pageNo: number,
    pageSize: number,
    batchNo: string,
): Promise<WrapperList<PurchasingLabelModel>> {
    return mainServerClient.get(`/purchasing_label/get_print_data/${distributedId}`, {
        pageNo,
        pageSize,
        distributedId,
        batchNo,
    });
}

interface PurchasingLabelStatsTaskResult {
    status: StatsTaskCacheResultStatsModel["status"];
    downloadUrl?: string;
}

/** 可多次调用重新生成新Excel */
export function exportPurchasingLabelExcel(distributedId: number) {
    return mainServerClient.get<PurchasingLabelStatsTaskResult>(`/purchasing_label/async_export/${distributedId}`);
}

/** 获取异步导出拿货标签Excel的结果 */
export function getExportPurchasingLabelResult(distributedTaskId: number) {
    return mainServerClient.get<PurchasingLabelStatsTaskResult>(
        `/purchasing_label/get_export_result/${distributedTaskId}`,
    );
}

export interface PurchasingLabelStatsTask {
    companyId?: number | null;
    /**
     * 任务创建时间
     */
    created?: null | string;
    /**
     * 分布式任务ID
     */
    distributedTaskId?: number | null;
    /**
     * 上传文件的下载链接
     */
    downloadUrl?: null | string;
    /**
     * 排除的orderItemId
     */
    excludedOrderItemIds?: number[] | null;
    /**
     * 排除的订单类型列表
     */
    excludeTypes?: PurchasingLabelStatisticsExcludeType[] | null;
    /**
     * 需导出的字段信息
     */
    exportItems?: PurchasingLabelStatisticsExportItem[] | null;
    id?: number | null;
    /**
     * 市场
     */
    market?: null | string;
    /**
     * 选中的订单ID列表
     * 用于勾选订单进行统计
     */
    orderIds?: number[] | null;
    /**
     * 支付结束时间
     */
    paidEnd?: null | string;
    /**
     * 支付开始时间
     */
    paidStart?: null | string;
    /**
     * 旗帜
     */
    sellerFlags?: OrderSellerRemarksFlag[] | null;
    /**
     * 勾选的店铺ID列表
     */
    shopIds?: number[] | null;
    /**
     * 任务状态
     */
    status?: "COMPLETED" | "ERROR" | "PENDING" | "RUNNING";
    /**
     * 排除不可达区域
     */
    undeliverableArea?: UndeliverableArea;
    /**
     * 档口
     */
    wholesaler?: null | string;

    /**
     * 表示该任务是否被操作过删除SKU信息
     */
    anyProductSkuDeleted?: boolean;
}

/** 获取拿货标签导出记录 */
export function getPurchasingExportTaskList(pageNo: number, pageSize: number) {
    return mainServerClient.get<WrapperList<PurchasingLabelStatsTask>>("/purchasing_label/list_stats_tasks", {
        pageNo,
        pageSize,
    });
}

export interface PurchasingLabelProductSkuUpdatedInfo {
    productOriginalId: string;
    productExternalId: string;
    skuOriginalId: string;
    skuProperties: string;
    skuExternalId: string;
    /**  删除不用传该值 */
    newQuantity?: number;
    updateType: ProductSkuUpdatedInfoType;
}

export function deletePurchasingLabel(
    distributedTaskId: number,
    productSkuUpdatedInfos: PurchasingLabelProductSkuUpdatedInfo[],
) {
    return mainServerClient.post(`/purchasing_label/delete/${distributedTaskId}`, { productSkuUpdatedInfos });
}

/** 获取拿货标签导出字段 */
export function getPurchasingLabelStatisticsExportItems() {
    return mainServerClient.get<PurchasingLabelStatisticsExportItem[]>("/stats/setting/purchasing_label_fields");
}

/** 保存拿货标签导出字段 */
export function savePurchasingLabelStatisticsExportItems(exportItems: PurchasingLabelStatisticsExportItem[]) {
    return mainServerClient.post("/stats/setting/save_purchasing_label_export_fields", { exportItems });
}
